import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SidePanel } from '../components/SidePanel';
import { DealFormModal } from '../components/DealFormModal';
import { DealDetail } from './DealDetailPage';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ExternalLink, Loader2, Pencil, Plus, X } from 'lucide-react';
import { vehicleApi } from '../services/vehicleApi';
import { dealApi } from '../services/dealApi';
import { companyApi } from '../services/companyApi';
import { cashflowApi } from '../services/cashflowApi';
import {
  KIND_LABEL, KIND_STYLE, LEGAL_FORMS, LegalForm, Participant,
  ParticipantForm, STATUS_LABEL, VEHICLE_STATUSES, Vehicle, VehicleStatus, shortWon, won, FEE_BASE_LABEL, FeeBase,
} from '../types/vehicle';
import { Deal, STAGE_STYLE, Valuation } from '../types/deal';
import { Company } from '../types/company';
import { Cashflow, KIND_META } from '../types/cashflow';
import { CompliancePanel } from '../components/CompliancePanel';
import { computeMetrics, mult, pct } from '../lib/metrics';
import { capitalRows, ledgerBalance } from '../lib/capital';
import { CashflowFormModal } from '../components/CashflowFormModal';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { FundLifecycle } from '../components/FundLifecycle';
import { FundProfile } from '../components/FundProfile';
import { FundBasics } from '../components/FundBasics';

/**
 * 자금원 상세 — 통장 하나의 전부.
 *
 * 위에서부터 **성적 → 누구 돈인가(참여자 계정) → 어디에 넣었나(투자 건) → 최근 원장**.
 * 참여자 계정은 LP 에게 「당신 돈이 지금 어떻게 됐나」를 답하는 자리다.
 *
 * ⚠️ 원장 잔액은 **실제 통장 잔액과 맞춰 보라고** 보여준다. 다르면 원장에 빠진 줄이 있다.
 */
/** 주소로 들어올 때(/vehicles/:id) — 대시보드 · 알림 링크 등. 목록에서는 오른쪽 패널로 연다 */
export const VehicleDetailPage: React.FC = () => {
  const { id } = useParams();
  return <VehicleDetail vehicleId={Number(id)} />;
};

/** 자금원 상세 본문 — 화면 전체(VehicleDetailPage) · 오른쪽 패널(목록) 둘 다 이걸 쓴다 */
export const VehicleDetail: React.FC<{ vehicleId: number; inPanel?: boolean }> = ({ vehicleId, inPanel = false }) => {
  const navigate = useNavigate();

  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [parts, setParts] = useState<Participant[]>([]);
  const [allParts, setAllParts] = useState<Participant[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [allDeals, setAllDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [values, setValues] = useState<Record<number, Valuation>>({});
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [partOpen, setPartOpen] = useState(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const [tourRun, setTourRun] = useState(false);
  const [dealOpen, setDealOpen] = useState(false);
  const [selDeal, setSelDeal] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, ps, ds, cs, vs, fs, lv] = await Promise.all([
        vehicleApi.get(vehicleId), vehicleApi.allParticipants(), dealApi.list(),
        companyApi.list(), vehicleApi.list(), cashflowApi.list(), dealApi.latestValues(),
      ]);
      setVehicle(v ?? null);
      setAllParts(ps);
      setParts(ps.filter((p) => p.vehicleId === vehicleId));
      setAllDeals(ds);
      setDeals(ds.filter((d) => d.vehicleId === vehicleId));
      setCompanies(cs);
      setVehicles(vs);
      setFlows(fs.filter((f) => f.vehicleId === vehicleId));
      setValues(lv);
    } finally { setLoading(false); }
  }, [vehicleId]);

  useEffect(() => { load(); }, [load]);

  const cName = (cid: number) => companies.find((c) => c.id === cid)?.name ?? '—';
  const closed = (d: Deal) => d.stage === '회수' || d.stage === '상각';

  const residual = deals.filter((d) => !closed(d)).reduce((s, d) => s + (values[d.id]?.ourValue ?? 0), 0);
  // 평가액 없는 보유 건 — 있으면 TVPI · IRR 을 「—」로(0 으로 세면 전액 손실처럼 보인다)
  const unvalued = deals.filter((d) => !closed(d) && !values[d.id]).length;
  const m = useMemo(() => {
    const x = computeMetrics(flows, residual);
    return unvalued ? { ...x, tvpi: null, irr: null } : x;
  }, [flows, residual, unvalued]);
  const capital = useMemo(() => capitalRows(parts, flows), [parts, flows]);
  const balance = ledgerBalance(flows);

  const dealRows = deals.map((d) => {
    const mine = flows.filter((f) => f.dealId === d.id);
    const invested = mine.filter((f) => f.kind === 'investment').reduce((s, f) => s + f.amount, 0);
    const returned = mine.filter((f) => f.kind === 'exit' || f.kind === 'dividend').reduce((s, f) => s + f.amount, 0);
    const res = closed(d) ? 0 : (values[d.id]?.ourValue ?? 0);
    return { d, invested, returned, res, moic: invested > 0 ? (returned + res) / invested : null };
  });

  const TOUR: TourStep[] = useMemo(() => [
    {
      name: 'head',
      target: '[data-tour="head"]',
      title: '자금원 하나의 전부',
      content: '통장 하나를 한 화면에 모았습니다. 수정에서 법적 형태 · 사업자번호 · 통장 명의 · 종료를 채웁니다.',
    },
    {
      name: 'cards',
      target: '[data-tour="cards"]',
      title: '이 통장의 성적',
      content: '들어온 출자, 나간 투자, 돌아온 회수, 아직 안 판 평가액과 그 배수입니다. 전부 원장에서 계산합니다.',
    },
    {
      name: 'balance',
      target: '[data-tour="balance"]',
      title: '원장 잔액',
      content: '원장에 적힌 들어온 돈 − 나간 돈입니다. 실제 통장 잔액과 다르면 원장에 빠진 줄이 있다는 뜻입니다.',
      placement: 'left',
    },
    {
      name: 'lifecycle',
      target: '[data-tour="lifecycle"]',
      title: '라이프사이클 · 시뮬레이션',
      content: '결성 → 투자기간 종료 → 만기와 남은 날입니다. 오른쪽은 남은 평가액을 몇 배에 판다면 TVPI 가 얼마가 되는지 가정해 봅니다.',
    },
    {
      name: 'capital',
      target: '[data-tour="capital"]',
      title: '참여자 계정',
      content: '참여자마다 약정 · 납입 · 미납 · 돌려받은 돈 · 지분입니다. 지분은 규약에 정한 비율이 있으면 그것을, 없으면 납입 비율을 씁니다.',
    },
    {
      name: 'deals',
      target: '[data-tour="deals"]',
      title: '이 통장에서 나간 투자',
      content: '이 자금원의 투자 건입니다. 누르면 오른쪽에 건 상세가 열립니다. 「투자 건 추가」는 이 자금원이 골라진 채로 열립니다.',
    },
    {
      name: 'flows',
      target: '[data-tour="flows"]',
      title: '최근 원장',
      content: '이 통장의 최근 기록입니다. 「기록 추가」는 이 자금원이 골라진 채로 열립니다.',
    },
  ], []);

  if (loading && !vehicle) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> 불러오는 중...
      </div>
    );
  }
  if (!vehicle) {
    return <div className="p-6 lg:p-8"><p className="text-sm text-slate-500">자금원을 찾을 수 없습니다.</p></div>;
  }

  const unchecked = vehicle.kind === 'entrusted' && !vehicle.legalForm;
  const hasContribution = flows.some((f) => f.kind === 'contribution');
  const committedTotal = parts.reduce((s, p) => s + (p.committedAmount ?? 0), 0);
  const recent = flows.slice(0, 8);
  const pName = (pid: number | null) => parts.find((p) => p.id === pid)?.name ?? null;

  return (
    <div className={inPanel ? '' : 'p-6 lg:p-8 max-w-6xl'}>
      {!inPanel && (
        <button onClick={() => navigate('/vehicles')}
          className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-2">
          <ChevronLeft size={14} /> 자금원
        </button>
      )}

      <div data-tour="head" className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
            {vehicle.name}
            <TourButton onClick={() => setTourRun(true)} />
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
            <span className={`rounded-full px-2 py-0.5 font-semibold ${KIND_STYLE[vehicle.kind]}`}>{KIND_LABEL[vehicle.kind]}</span>
            {vehicle.legalForm
              ? <span className="rounded-full px-2 py-0.5 font-medium bg-slate-100 text-slate-600">{vehicle.legalForm}</span>
              : vehicle.kind === 'entrusted' && <span className="text-amber-700">법적 형태 확인 전</span>}
            <span className="rounded-full px-2 py-0.5 font-medium bg-slate-100 text-slate-600">{STATUS_LABEL[vehicle.status]}</span>
            <span className="text-slate-400">
              {vehicle.startedOn.replace(/-/g, '.')} 시작
              {vehicle.endedOn && ` · ${vehicle.endedOn.replace(/-/g, '.')} 종료`}
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {vehicle.accountName && <>통장 명의 <b className="font-medium text-slate-700">{vehicle.accountName}</b></>}
            {vehicle.bizNo && <> · {vehicle.bizNo}</>}
          </p>
          {vehicle.note && <p className="mt-1 text-xs text-slate-400 max-w-xl">{vehicle.note}</p>}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={() => setEditOpen(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
            <Pencil size={12} /> 수정
          </button>
          <button onClick={() => setFlowOpen(true)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700">
            <Plus size={13} /> 기록 추가
          </button>
        </div>
      </div>

      {unchecked && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-amber-900">
            <AlertTriangle size={14} /> 법적 형태 확인 전 <span className="font-normal text-amber-800">— 사업자등록증 · 통장 명의 · 규약/자문계약서</span>
          </h2>
        </div>
      )}

      <div data-tour="cards" className={`grid grid-cols-2 sm:grid-cols-4 ${inPanel ? '' : 'lg:grid-cols-8'} gap-2 mb-5`}>
        {([
          ['출자', hasContribution ? shortWon(m.contributed) : '—', committedTotal > 0 ? `약정의 ${Math.round((m.contributed / committedTotal) * 100)}%` : ''],
          ['투자', shortWon(m.invested), hasContribution && m.contributed > 0 ? `출자의 ${Math.round((m.invested / m.contributed) * 100)}%` : ''],
          ['회수', m.returned > 0 ? shortWon(m.returned) : '—', ''],
          ['평가', residual > 0 ? shortWon(residual) : '—', unvalued ? `평가 없음 ${unvalued}건` : ''],
          ['DPI', mult(m.dpi), ''],
          ['TVPI', mult(m.tvpi), ''],
          ['IRR', pct(m.irr), ''],
          ['보수', m.fee > 0 ? shortWon(m.fee) : '—', ''],
        ] as const).map(([k, v, note]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-lg px-3 py-2.5">
            <div className="text-xs text-slate-400">{k}</div>
            <div className="mt-0.5 text-base font-bold text-slate-900 tabular-nums">{v}</div>
            {note && <div className="text-xs text-slate-400">{note}</div>}
          </div>
        ))}
      </div>

      <div data-tour="balance" className="mb-5 flex flex-wrap items-baseline justify-between gap-2 bg-white border border-slate-200 rounded-lg px-4 py-3">
        <div>
          <div className="text-xs text-slate-400">원장 잔액</div>
          <div className={`text-lg font-bold tabular-nums ${balance < 0 ? 'text-red-600' : 'text-slate-900'}`}>{won(balance)}</div>
        </div>
      </div>

      <FundBasics vehicle={vehicle} contributed={m.contributed} invested={m.invested} fee={m.fee}
        expense={flows.filter((f) => f.kind === 'expense').reduce((s, f) => s + f.amount, 0)} distributed={m.distributed} balance={balance} />
      <CompliancePanel vehicleId={vehicle.id} days={180}
        version={[vehicle.vehicleType, vehicle.registeredOn, vehicle.formedOn, vehicle.fiscalYearEnd, vehicle.status].join('|')} />
      <FundLifecycle vehicle={vehicle} invested={m.invested} returned={m.returned} residual={residual} />
      <FundProfile key={`${vehicle.purpose ?? ''}|${(vehicle.restrictions ?? []).join('|')}`} vehicleId={vehicle.id} deals={allDeals} companies={companies} flows={flows} values={values} />

      {/* 참여자 계정 */}
      <section data-tour="capital" className="bg-white border border-slate-200 rounded-lg mb-5">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">참여자 계정</h2>
          </div>
          <button onClick={() => setPartOpen(true)}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50">
            <Plus size={11} /> 참여자 추가
          </button>
        </div>
        {capital.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">참여자가 없습니다.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs text-slate-400">
                  <th className="text-left font-medium px-4 py-2">참여자</th>
                  <th className="text-right font-medium px-2 py-2">약정</th>
                  <th className="text-right font-medium px-2 py-2">납입</th>
                  <th className="text-right font-medium px-2 py-2">미납</th>
                  <th className="text-right font-medium px-2 py-2">분배 받음</th>
                  <th className="text-right font-medium px-2 py-2">보수</th>
                  <th className="text-right font-medium px-4 py-2">지분</th>
                </tr>
              </thead>
              <tbody>
                {capital.map((r) => (
                  <tr key={r.p.id} className="border-b border-slate-50 last:border-b-0">
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-slate-900">{r.p.name}</span>
                      {r.p.isManager && <span className="ml-1.5 rounded-full px-1.5 py-0.5 text-xs bg-indigo-50 text-indigo-600">운용</span>}
                      {r.p.note && <div className="text-xs text-slate-400">{r.p.note}</div>}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{r.committed === null ? <span className="text-slate-300">모름</span> : shortWon(r.committed)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums font-medium text-slate-900 whitespace-nowrap">{shortWon(r.paidIn)}</td>
                    <td className={`px-2 py-2.5 text-right tabular-nums whitespace-nowrap ${r.unpaid ? 'text-amber-700 font-medium' : 'text-slate-400'}`}>
                      {r.unpaid === null ? '—' : r.unpaid > 0 ? shortWon(r.unpaid) : '없음'}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{shortWon(r.distributed)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-amber-700 whitespace-nowrap">{r.fee > 0 ? shortWon(r.fee) : '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                      {r.share === null ? <span className="text-slate-300">—</span> : (
                        <>
                          <span className="text-slate-900">{(r.share * 100).toFixed(1)}%</span>
                          <div className="text-xs text-slate-400">{r.shareFrom === 'rule' ? '규약' : '납입 비율'}</div>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className={`grid gap-4 ${inPanel ? '' : 'lg:grid-cols-2'}`}>
        <section data-tour="deals" className="bg-white border border-slate-200 rounded-lg">
          <h2 className="flex items-center justify-between text-sm font-semibold text-slate-900 px-4 py-3 border-b border-slate-100">
            <span>투자 건 {deals.length}</span>
            <button type="button" onClick={() => setDealOpen(true)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-200 text-slate-700 hover:bg-slate-50">
              <Plus size={12} /> 투자 건 추가
            </button>
          </h2>
          {dealRows.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">이 자금원에서 나간 투자가 없습니다.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {dealRows.map(({ d, invested, moic }) => (
                  <tr key={d.id} onClick={() => setSelDeal(d.id)} className="border-b border-slate-50 last:border-b-0 cursor-pointer hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <button type="button" className="font-medium text-slate-900 hover:text-indigo-600 text-left">{cName(d.companyId)}</button>
                      <div className="text-xs text-slate-400">{d.securityKind ?? '증권 모름'}</div>
                    </td>
                    <td className="px-2 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_STYLE[d.stage]}`}>{d.stage}</span></td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-slate-900 whitespace-nowrap">{shortWon(invested)}</td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-semibold whitespace-nowrap ${
                      moic === null ? 'text-slate-400' : moic >= 1 ? 'text-emerald-700' : 'text-red-600'
                    }`}>{mult(moic)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section data-tour="flows" className="bg-white border border-slate-200 rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">최근 원장</h2>
            <Link to={`/ledger?vehicle=${vehicle.id}`} className="text-xs text-indigo-600 hover:underline">전부 보기 ({flows.length})</Link>
          </div>
          {recent.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">아직 기록이 없습니다.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {recent.map((f) => (
                  <tr key={f.id} className="border-b border-slate-50 last:border-b-0">
                    <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap tabular-nums">{f.occurredOn.replace(/-/g, '.')}</td>
                    <td className="px-2 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${KIND_META[f.kind].style}`}>{KIND_META[f.kind].label}</span>
                    </td>
                    <td className="px-2 py-2.5 text-xs text-slate-600">
                      {(f.dealId !== null && cName(allDeals.find((d) => d.id === f.dealId)?.companyId ?? -1)) || pName(f.participantId) || '—'}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-medium whitespace-nowrap tabular-nums ${f.direction === 'in' ? 'text-emerald-700' : 'text-slate-900'}`}>
                      {f.direction === 'in' ? '+' : '−'}{won(f.amount).slice(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <DealFormModal open={dealOpen} vehicles={vehicles} companies={companies} vehicleId={vehicle.id}
        onClose={() => setDealOpen(false)} onSaved={() => { setDealOpen(false); load(); }} />
      {selDeal !== null && (
        <SidePanel onClose={() => { setSelDeal(null); load(); }}
          header={<div className="flex items-center gap-2 text-xs text-slate-500">투자 건 상세
            <Link to={`/deals/${selDeal}`} className="inline-flex items-center gap-0.5 text-indigo-600 hover:underline">전체 화면 <ExternalLink size={11} /></Link></div>}>
          <DealDetail key={selDeal} dealId={selDeal} inPanel />
        </SidePanel>
      )}
      <VehicleEditModal open={editOpen} vehicle={vehicle} onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); load(); }} />
      <ParticipantModal open={partOpen} vehicleId={vehicle.id} onClose={() => setPartOpen(false)}
        onSaved={() => { setPartOpen(false); load(); }} />
      <CashflowFormModal open={flowOpen} vehicles={vehicles} parts={allParts} deals={allDeals} companies={companies}
        initial={{ vehicleId: vehicle.id, kind: vehicle.kind === 'own' ? 'investment' : 'contribution' }}
        onClose={() => setFlowOpen(false)} onSaved={() => { setFlowOpen(false); load(); }} />

      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};

const input = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-indigo-400 focus:outline-none';
const label = 'block text-xs font-medium text-slate-500 mb-1';

const num = (x: string) => (x.trim() === '' ? null : Number(x));
const pctIn = (x: string) => (x.trim() === '' ? null : Math.round(Number(x) * 100) / 10000);

/** 규약 값 → 저장 본문. 고유계정은 창업기획자 등록일만 */
function termsBody(t: Record<string, string>, own: boolean): Partial<Vehicle> {
  if (own) return { registeredOn: t.registeredOn || null };
  return {
    vehicleType: t.vehicleType.trim() || null, formedOn: t.formedOn || null, registeredOn: t.registeredOn || null,
    investEndOn: t.investEndOn || null, maturityOn: t.maturityOn || null, fiscalYearEnd: t.fiscalYearEnd.trim() || null,
    mgmtFeeRate: pctIn(t.mgmtFeeRate), mgmtFeeBase: (t.mgmtFeeBase || null) as FeeBase | null, mgmtFeeBaseAfter: (t.mgmtFeeBaseAfter || null) as FeeBase | null,
    mgmtFeePrepaidYears: num(t.mgmtFeePrepaidYears), carryRate: pctIn(t.carryRate), hurdleRate: pctIn(t.hurdleRate),
    paymentMethod: (t.paymentMethod || null) as Vehicle['paymentMethod'],
    vicsCode: t.vicsCode.trim() || null,
    auditor: t.auditor.trim() || null,
    lossOrder: t.lossOrder.trim() || null,
    purpose: t.purpose.trim() || null,
    // 제한조건 — 줄마다 하나
    restrictions: t.restrictions.split(/\r?\n/).map((x) => x.trim()).filter(Boolean),
  };
}

const TermsFields: React.FC<{ terms: Record<string, string>; setTerms: React.Dispatch<React.SetStateAction<Record<string, string>>>; own: boolean }> = ({ terms, setTerms, own }) => {
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setTerms((t) => ({ ...t, [k]: e.target.value }));
  const date = (k: string, text: string) => (
    <div>
      <label className={label} htmlFor={`ve-${k}`}>{text}</label>
      <input id={`ve-${k}`} type="date" className={input} value={terms[k] ?? ''} onChange={set(k)} />
    </div>
  );
  if (own) {
    return <div className="grid grid-cols-2 gap-3">{date('registeredOn', '창업기획자 등록일')}</div>;
  }
  const bases = (k: string, text: string, empty: string) => (
    <div>
      <label className={label} htmlFor={`ve-${k}`}>{text}</label>
      <select id={`ve-${k}`} className={input} value={terms[k] ?? ''} onChange={set(k)}>
        <option value="">{empty}</option>
        {(Object.keys(FEE_BASE_LABEL) as FeeBase[]).map((b) => <option key={b} value={b}>{FEE_BASE_LABEL[b]}</option>)}
      </select>
    </div>
  );
  const rate = (k: string, text: string) => (
    <div>
      <label className={label} htmlFor={`ve-${k}`}>{text}</label>
      <input id={`ve-${k}`} type="number" min={0} step={0.1} className={input + ' text-right'} value={terms[k] ?? ''} onChange={set(k)} />
    </div>
  );
  return (
    <fieldset className="border-t border-slate-100 pt-3 space-y-3">
      <legend className="text-xs font-semibold text-slate-700 pr-2">규약</legend>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label} htmlFor="ve-vehicleType">투자기구</label>
          <select id="ve-vehicleType" className={input} value={terms.vehicleType ?? ''} onChange={set('vehicleType')}>
            <option value="">선택</option>
            {['개인투자조합', '벤처투자조합', '기타'].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
        <div>
          <label className={label} htmlFor="ve-fiscalYearEnd">사업연도 말 (MM-DD)</label>
          <input id="ve-fiscalYearEnd" className={input} value={terms.fiscalYearEnd ?? ''} onChange={set('fiscalYearEnd')} placeholder="12-31" />
        </div>
        {date('formedOn', '결성일')}
        {date('registeredOn', '등록일')}
        {date('investEndOn', '투자기간 종료')}
        {date('maturityOn', '존속기간 만료')}
        {rate('mgmtFeeRate', '관리보수 (연 %)')}
        <div>
          <label className={label} htmlFor="ve-mgmtFeePrepaidYears">선취 연수</label>
          <input id="ve-mgmtFeePrepaidYears" type="number" min={1} max={10} className={input + ' text-right'} value={terms.mgmtFeePrepaidYears ?? ''} onChange={set('mgmtFeePrepaidYears')} placeholder="없음" />
        </div>
        {bases('mgmtFeeBase', '관리보수 기준', '선택')}
        {bases('mgmtFeeBaseAfter', '투자기간 뒤 기준', '같음')}
        {rate('carryRate', '성과보수 (%)')}
        {rate('hurdleRate', '기준수익률 (연 %)')}
        <div>
          <label className={label} htmlFor="ve-vicsCode">VICS 조합 코드 <span className="text-slate-400">— 1328 조회</span></label>
          <input id="ve-vicsCode" className={input} value={terms.vicsCode ?? ''} onChange={set('vicsCode')} />
        </div>
        <div>
          <label className={label} htmlFor="ve-paymentMethod">납입 방식</label>
          <select id="ve-paymentMethod" className={input} value={terms.paymentMethod ?? ''} onChange={set('paymentMethod')}>
            <option value="">미정</option>
            <option value="lump_sum">일시납</option>
            <option value="installment">분할납</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label} htmlFor="ve-auditor">회계감사인</label>
          <input id="ve-auditor" className={input} value={terms.auditor ?? ''} onChange={set('auditor')} placeholder="예) ○○회계법인" />
        </div>
        <div>
          <label className={label} htmlFor="ve-lossOrder">우선손실충당순서 <span className="text-slate-400">— 규약 문장</span></label>
          <input id="ve-lossOrder" className={input} value={terms.lossOrder ?? ''} onChange={set('lossOrder')} placeholder="예) 업무집행조합원 출자분부터" />
        </div>
      </div>
      <div>
        <label className={label} htmlFor="ve-purpose">투자목적</label>
        <textarea id="ve-purpose" className={input + ' h-16 resize-none'} value={terms.purpose ?? ''} onChange={set('purpose')}
          placeholder="예) ㈜디앤라이프 보통주 투자 100%" />
      </div>
      <div>
        <label className={label} htmlFor="ve-restrictions">투자 제한조건 <span className="text-slate-400">— 줄마다 하나</span></label>
        <textarea id="ve-restrictions" className={input + ' h-20 resize-none'} value={terms.restrictions ?? ''} onChange={set('restrictions')}
          placeholder={'예) 상장사 투자 10% 이내\n신주만 취득'} />
      </div>
    </fieldset>
  );
};

/**
 * 자금원 수정 — 등록 때 몰랐던 것을 나중에 채운다.
 * 자금 구분(`kind`)은 바꾸지 않는다. 바뀌면 원장 전체의 의미가 바뀌므로 새로 만든다.
 */
const VehicleEditModal: React.FC<{
  open: boolean; vehicle: Vehicle; onClose: () => void; onSaved: () => void;
}> = ({ open, vehicle, onClose, onSaved }) => {
  const [legalForm, setLegalForm] = useState<LegalForm | ''>('');
  const [bizNo, setBizNo] = useState('');
  const [accountName, setAccountName] = useState('');
  const [status, setStatus] = useState<VehicleStatus>('active');
  const [endedOn, setEndedOn] = useState('');
  const [note, setNote] = useState('');
  // 규약 값 — 조합 규약 · 창업기획자 등록증에서 옮긴다(2026-10-26). % 는 화면에서만 % 로 쓴다
  const [terms, setTerms] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  // 실패하면 창 안에 이유를 보인다 — 던지면 단추가 아무 반응 없이 멈춘다(실사용 테스트 7회차)
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const pctOf = (x?: number | null) => (x == null ? '' : String(Math.round(x * 10000) / 100));
    setTerms({
      vehicleType: vehicle.vehicleType ?? '', formedOn: vehicle.formedOn ?? '', registeredOn: vehicle.registeredOn ?? '',
      investEndOn: vehicle.investEndOn ?? '', maturityOn: vehicle.maturityOn ?? '', fiscalYearEnd: vehicle.fiscalYearEnd ?? '',
      mgmtFeeRate: pctOf(vehicle.mgmtFeeRate), mgmtFeeBase: vehicle.mgmtFeeBase ?? '', mgmtFeeBaseAfter: vehicle.mgmtFeeBaseAfter ?? '',
      mgmtFeePrepaidYears: vehicle.mgmtFeePrepaidYears == null ? '' : String(vehicle.mgmtFeePrepaidYears),
      carryRate: pctOf(vehicle.carryRate), hurdleRate: pctOf(vehicle.hurdleRate),
      paymentMethod: vehicle.paymentMethod ?? '', vicsCode: vehicle.vicsCode ?? '', auditor: vehicle.auditor ?? '', lossOrder: vehicle.lossOrder ?? '', purpose: vehicle.purpose ?? '', restrictions: (vehicle.restrictions ?? []).join('\n'),
    });
    setLegalForm(vehicle.legalForm ?? '');
    setBizNo(vehicle.bizNo ?? '');
    setAccountName(vehicle.accountName ?? '');
    setStatus(vehicle.status);
    setEndedOn(vehicle.endedOn ?? '');
    setNote(vehicle.note);
  }, [open, vehicle]);

  if (!open) return null;

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await vehicleApi.update(vehicle.id, {
        legalForm: legalForm === '' ? null : legalForm,
        bizNo: bizNo.trim() || null,
        accountName: accountName.trim() || null,
        status,
        // 운용 중으로 되돌리면 종료일도 지운다
        endedOn: status === 'closed' ? endedOn || null : null,
        note: note.trim(),
        ...termsBody(terms, vehicle.kind === 'own'),
      });
      onSaved();
    } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  };

  return (
    <SidePanel onClose={onClose} bare>
      <div className="flex flex-col min-h-full">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-900">자금원 수정</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {vehicle.kind === 'entrusted' && (
            <div>
              <label className={label}>운용 형태</label>
              <div className="flex flex-wrap gap-2">
                {(['', ...LEGAL_FORMS] as const).map((f) => (
                  <button key={f || 'none'} type="button" onClick={() => setLegalForm(f)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                      legalForm === f ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                    }`}>{f || '미정'}</button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="ve-acc">예금주(계좌 명의)</label>
              <input id="ve-acc" className={input} value={accountName} onChange={(e) => setAccountName(e.target.value)} />
            </div>
            <div>
              <label className={label} htmlFor="ve-biz">{legalForm === '조합' ? '조합 고유번호' : '사업자번호'}</label>
              <input id="ve-biz" className={input} value={bizNo} onChange={(e) => setBizNo(e.target.value)} placeholder="000-00-00000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="ve-status">상태</label>
              <select id="ve-status" className={input} value={status} onChange={(e) => setStatus(e.target.value as VehicleStatus)}>
                {VEHICLE_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
            {status === 'closed' && (
              <div>
                <label className={label} htmlFor="ve-end">종료일</label>
                <input id="ve-end" type="date" className={input} value={endedOn} onChange={(e) => setEndedOn(e.target.value)} />
              </div>
            )}
          </div>
          <TermsFields terms={terms} setTerms={setTerms} own={vehicle.kind === 'own'} />
          <div>
            <label className={label} htmlFor="ve-note">비고</label>
            <textarea id="ve-note" className={input + ' h-16 resize-none'} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <div className="mt-auto sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          {err && <p role="alert" className="mr-auto text-xs text-red-600">{err}</p>}
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">취소</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </SidePanel>
  );
};

const ParticipantModal: React.FC<{
  open: boolean; vehicleId: number; onClose: () => void; onSaved: () => void;
}> = ({ open, vehicleId, onClose, onSaved }) => {
  const [form, setForm] = useState<ParticipantForm>({ id: 0, name: '', isManager: false, committedAmount: null });
  const [saving, setSaving] = useState(false);
  // 실패하면 창 안에 이유를 보인다 — 던지면 단추가 아무 반응 없이 멈춘다(실사용 테스트 7회차)
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (open) setForm({ id: 0, name: '', isManager: false, committedAmount: null }); }, [open]);
  if (!open) return null;

  const save = async () => {
    setSaving(true); setErr(null);
    try { await vehicleApi.addParticipant(vehicleId, form); onSaved(); } catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  };

  return (
    <SidePanel onClose={onClose} bare>
      <div className="flex flex-col min-h-full">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">참여자 추가</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className={label} htmlFor="p-name">이름</label>
            <input id="p-name" className={input} value={form.name} placeholder="기업명 또는 GRK"
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className={label} htmlFor="p-amt">약정액 (원) <span className="text-slate-400">— 모르면 비움</span></label>
            <input id="p-amt" type="number" min={0} step={10000000} className={input + ' text-right'}
              value={form.committedAmount ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, committedAmount: e.target.value === '' ? null : Number(e.target.value) }))} />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.isManager} onChange={(e) => setForm((f) => ({ ...f, isManager: e.target.checked }))} />
            운용하는 쪽 (GRK)
          </label>
        </div>
        <div className="mt-auto sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          {err && <p role="alert" className="mr-auto text-xs text-red-600">{err}</p>}
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">취소</button>
          <button onClick={save} disabled={!form.name.trim() || saving}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40">
            {saving ? '저장 중...' : '추가'}
          </button>
        </div>
      </div>
    </SidePanel>
  );
};
